import { useState } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import SEO from '@/components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await api.post('/enquiries', formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const offices = [
    {
      title: 'Puducherry Office',
      address: 'Vallalar Velai Vaaipu Sevai Iyakkam, No-122, 2nd Floor, Venugopal Nagar, Opp to Saibaba temple, Kirumambakkam, Puducherry UT, India – 607402',
      phone: '+91 82705 27992',
      email: 'info@vvvsi.com'
    },
    {
      title: 'Chennai Office',
      address: 'Vallalar Velai Vaaipu Sevai Iyakkam, No 34/24 Rajendran street, Thiru Nagar, Valasaravakkam, Chennai, Tamilnadu - 600087',
      phone: '+91 82705 27992',
      email: 'info@vvvsi.com'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <SEO 
        title="Contact Us | VVVSI - Vallalar Velai Vaaipu Sevai Iyakkam"
        description="Contact VVVSI for job placement services, career guidance, and employment opportunities. Offices in Puducherry and Chennai. Call +91 82705 27992 or email info@vvvsi.com"
        keywords="VVVSI contact, job portal contact, Puducherry office, Chennai office, employment services contact"
        url="/contact"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Contact Us
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl">
              We're here to help! If you have any questions, concerns, or feedback, please feel free to reach out to us.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="p-8 border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      data-testid="contact-name-input"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      data-testid="contact-message-input"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Enter your message"
                      rows={5}
                    />
                  </div>
                  <Button 
                    data-testid="contact-submit-btn"
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Office Locations */}
            <div className="space-y-6">
              {offices.map((office, index) => (
                <Card key={index} className="p-6 border-slate-200">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                    {office.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600 text-sm">{office.address}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <a href={`tel:${office.phone}`} className="text-slate-600 text-sm hover:text-teal-600">
                        {office.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <a href={`mailto:${office.email}`} className="text-slate-600 text-sm hover:text-teal-600">
                        {office.email}
                      </a>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Map Placeholder */}
              <Card className="p-6 border-slate-200 bg-slate-100">
                <div className="aspect-video rounded-lg bg-slate-200 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <MapPin className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">View on Google Maps</p>
                    <a 
                      href="https://maps.google.com/?q=Kirumambakkam,Puducherry"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:underline text-sm"
                    >
                      Open in Maps
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
